import Immutable from 'immutable';
import { RootStateOrAny } from 'react-redux';
import { AnyAction } from 'redux';

import {
  CREATE_OR_UPDATE_CONVERSATION_REQUEST,
  CREATE_OR_UPDATE_CONVERSATION_FAILURE,
  CREATE_OR_UPDATE_CONVERSATION_SUCCESS,
  GET_CONVERSATIONS_REQUEST,
  GET_CONVERSATIONS_FAILURE,
  GET_CONVERSATIONS_SUCCESS,
  GET_MESSAGES_REQUEST,
  GET_MESSAGES_FAILURE,
  GET_MESSAGES_SUCCESS,
  GET_CONVERSATION_BY_ID_REQUEST,
  GET_CONVERSATION_BY_ID_FAILURE,
  GET_CONVERSATION_BY_ID_SUCCESS,
  GET_CONVERSATION_BY_ID_SUCCESS_ADMIN,
  UPDATE_CONVERSATION_READ_STATUS_FAILURE,
  UPDATE_CONVERSATION_READ_STATUS_REQUEST,
  UPDATE_CONVERSATION_READ_STATUS_SUCCESS,
  UPDATE_CONVERSATION_READ_STATUS_SUCCESS_ADMIN,
  GET_FILE_TO_BLOB_REQUEST,
  GET_FILE_TO_BLOB_SUCCESS,
  GET_FILE_TO_BLOB_FAILURE,
  PAGINATE_MESSAGES_REQUEST,
  PAGINATE_MESSAGES_SUCCESS,
  PAGINATE_MESSAGES_FAILURE,
  SET_DRAFT_MESSAGE,
  GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST,
  GET_CONVERSATIONS_WITH_INSTRUCTORS_SUCCESS,
  GET_CONVERSATIONS_WITH_INSTRUCTORS_FAILURE,
  SET_INSTRUCTOR_INACTIVE_STATUS,
} from './actionType';
import { SIGN_OUT_SUCCESS } from '../auth/actionType';

export const initialState = {
  conversations: {},
  messages: {},
  failedRequests: {},
  successfulRequests: {},
  currentFileBlob: {},
  requests: {},
  msgHasNextPage: {},
  drafts: {},
  sendingStatus: {},
  convosWithInstructors: {},
  adminConvo: {},
};

export type ConversationStore = typeof initialState;

export default (
  state: RootStateOrAny = initialState,
  action: AnyAction,
): object => {
  const { payload, type } = action;

  switch (type) {
    case CREATE_OR_UPDATE_CONVERSATION_REQUEST:
      return Immutable.fromJS(state)
        .updateIn(
          ['requests', CREATE_OR_UPDATE_CONVERSATION_REQUEST, 'pending'],
          () => true,
        )
        .toJS();
    case CREATE_OR_UPDATE_CONVERSATION_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', CREATE_OR_UPDATE_CONVERSATION_REQUEST],
          () => payload.response,
        )
        .updateIn(
          ['conversations', payload.response.conversation.id],
          () => payload.response.conversation,
        )
        .updateIn(['messages', payload.response.conversation.id], () => {
          const conversationId = payload.response.conversation.id;
          const messages =
            state.messages && state.messages[conversationId]
              ? [...state.messages[conversationId]]
              : [];

          return messages.concat(payload.response.message);
        })
        .updateIn(
          ['requests', CREATE_OR_UPDATE_CONVERSATION_REQUEST, 'pending'],
          () => false,
        )
        .updateIn(
          ['sendingStatus', CREATE_OR_UPDATE_CONVERSATION_REQUEST, 'failed'],
          () => false,
        )
        .toJS();
    case CREATE_OR_UPDATE_CONVERSATION_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['failedRequests', CREATE_OR_UPDATE_CONVERSATION_REQUEST],
          () => payload.error,
        )
        .updateIn(
          ['requests', CREATE_OR_UPDATE_CONVERSATION_REQUEST, 'pending'],
          () => false,
        )
        .updateIn(
          ['sendingStatus', CREATE_OR_UPDATE_CONVERSATION_REQUEST, 'failed'],
          () => true,
        )
        .toJS();
    case GET_CONVERSATIONS_REQUEST:
      return Immutable.fromJS(state)
        .updateIn(
          ['requests', GET_CONVERSATIONS_REQUEST, 'pending'],
          () => true,
        )
        .toJS();
    case GET_CONVERSATIONS_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', GET_CONVERSATIONS_REQUEST],
          () => payload.response,
        )
        .updateIn(['conversations'], () => payload.response)
        .updateIn(
          ['requests', GET_CONVERSATIONS_REQUEST, 'pending'],
          () => false,
        )
        .toJS();
    case GET_CONVERSATIONS_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['failedRequests', GET_CONVERSATIONS_REQUEST],
          () => payload.error,
        )
        .updateIn(
          ['requests', GET_CONVERSATIONS_REQUEST, 'pending'],
          () => false,
        )
        .toJS();
    case GET_MESSAGES_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', GET_MESSAGES_REQUEST],
          () => payload.response,
        )
        .updateIn(['messages', payload.response.id], () => {
          const conversationId = payload.response.id;
          let messages = [];
          try {
            const msgLength = state.messages[conversationId].length;
            let lastId = state.messages[conversationId][msgLength - 1].id;
            let msgId = payload.response.messages[0].id;

            if (lastId.includes('_')) {
              lastId = lastId.slice(0, -2);
            }
            if (msgId.includes('_')) {
              msgId = msgId.slice(0, -2);
            }
            lastId !== msgId
              ? (messages = [
                  ...state.messages[conversationId],
                  ...payload.response.messages,
                ])
              : (messages = [...state.messages[conversationId]]);
          } catch (err) {
            messages = [...payload.response.messages];
          }
          return messages;
        })
        .toJS();
    case GET_MESSAGES_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(['failedRequests', GET_MESSAGES_REQUEST], () => payload.error)
        .toJS();
    case GET_CONVERSATION_BY_ID_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', GET_CONVERSATION_BY_ID_REQUEST],
          () => payload.response,
        )
        .updateIn(
          ['convosWithInstructors', payload.response.id],
          () => payload.response,
        )
        .updateIn(
          ['conversations', payload.response.id],
          () => payload.response,
        )
        .toJS();
    case GET_CONVERSATION_BY_ID_SUCCESS_ADMIN:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', GET_CONVERSATION_BY_ID_REQUEST],
          () => payload.response,
        )
        .updateIn(['adminConvo'], () => payload.response)
        .updateIn(
          ['conversations', payload.response.id],
          () => payload.response,
        )
        .toJS();
    case GET_CONVERSATION_BY_ID_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['failedRequests', GET_CONVERSATION_BY_ID_REQUEST],
          () => payload.error,
        )
        .toJS();
    case UPDATE_CONVERSATION_READ_STATUS_REQUEST:
      return Immutable.fromJS(state)
        .updateIn(
          [
            'conversations',
            payload.conversationId,
            'previewMessageData',
            'unreadMessages',
            payload.userId,
          ],
          () => 0,
        )
        .toJS();
    case UPDATE_CONVERSATION_READ_STATUS_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', UPDATE_CONVERSATION_READ_STATUS_REQUEST],
          () => payload.response,
        )
        .updateIn(
          ['conversations', payload.response.id],
          () => payload.response,
        )
        .updateIn(
          ['convosWithInstructors', payload.response.id],
          () => payload.response,
        )
        .toJS();
    case UPDATE_CONVERSATION_READ_STATUS_SUCCESS_ADMIN:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', UPDATE_CONVERSATION_READ_STATUS_REQUEST],
          () => payload.response,
        )
        .updateIn(
          ['conversations', payload.response.id],
          () => payload.response,
        )
        .updateIn(['adminConvo'], () => {
          return {
            ...state.adminConvo,
            previewMessageData: {
              ...payload.response.previewMessageData,
            },
          };
        })
        .toJS();
    case UPDATE_CONVERSATION_READ_STATUS_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['failedRequests', UPDATE_CONVERSATION_READ_STATUS_REQUEST],
          () => payload.error,
        )
        .toJS();
    case GET_FILE_TO_BLOB_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['currentFileBlob', GET_FILE_TO_BLOB_REQUEST],
          () => payload.response,
        )
        .toJS();
    case GET_FILE_TO_BLOB_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['currentFileBlob', GET_FILE_TO_BLOB_REQUEST],
          () => payload.error,
        )
        .toJS();
    case PAGINATE_MESSAGES_REQUEST:
      return Immutable.fromJS(state)
        .updateIn(
          ['requests', PAGINATE_MESSAGES_REQUEST, 'pending'],
          () => true,
        )
        .toJS();
    case PAGINATE_MESSAGES_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', PAGINATE_MESSAGES_REQUEST],
          () => payload.response,
        )
        .updateIn(['messages', payload.response.id], () => {
          const conversationId = payload.response.id;
          const messages =
            state.messages && state.messages[conversationId]
              ? [...state.messages[conversationId]]
              : [];
          const msg = payload.response.messages.reverse();
          // @ts-ignore
          if (messages.length > 0 && msg.find(x => x.id === messages[0].id)) {
            return msg;
          }
          return msg.concat(messages);
        })
        .updateIn(
          ['msgHasNextPage', payload.response.id],
          () => payload.response.hasNextPage,
        )
        .updateIn(
          ['requests', PAGINATE_MESSAGES_REQUEST, 'pending'],
          () => false,
        )
        .toJS();
    case PAGINATE_MESSAGES_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['failedRequests', PAGINATE_MESSAGES_FAILURE],
          () => payload.error,
        )
        .updateIn(
          ['requests', PAGINATE_MESSAGES_REQUEST, 'pending'],
          () => false,
        )
        .toJS();
    case SIGN_OUT_SUCCESS:
      return Immutable.fromJS(state)
        .merge(state, initialState)
        .toJS();
    case SET_DRAFT_MESSAGE:
      return Immutable.fromJS(state)
        .updateIn(['drafts', payload.convoId], () => payload.params)
        .toJS();
    case GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST:
      return Immutable.fromJS(state)
        .updateIn(
          ['requests', GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST, 'pending'],
          () => true,
        )
        .toJS();
    case GET_CONVERSATIONS_WITH_INSTRUCTORS_SUCCESS:
      return Immutable.fromJS(state)
        .updateIn(
          ['successfulRequests', GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST],
          () => payload.response,
        )
        .updateIn(
          ['convosWithInstructors'],
          () => payload.response.conversations,
        )
        .updateIn(['adminConvo'], () => payload.response.adminConvo)
        .updateIn(
          ['requests', GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST, 'pending'],
          () => false,
        )
        .toJS();
    case GET_CONVERSATIONS_WITH_INSTRUCTORS_FAILURE:
      return Immutable.fromJS(state)
        .updateIn(
          ['requests', GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST, 'pending'],
          () => false,
        )
        .updateIn(
          ['failedRequests', GET_CONVERSATIONS_WITH_INSTRUCTORS_REQUEST],
          () => payload.error,
        )
        .toJS();
    case SET_INSTRUCTOR_INACTIVE_STATUS:
      return Immutable.fromJS(state)
        .updateIn(
          [
            'convosWithInstructors',
            payload.convoId,
            'participants',
            payload.instructorsId,
            'isInactive',
          ],
          () => payload.status,
        )
        .toJS();
    default:
      return state;
  }
};
